import React from 'react';
import type { JourneyCategory, JourneyEvent } from '../data/mock';

interface Props {
  event: JourneyEvent;
  side: 'left' | 'right';
  daysSinceStart: number;
  highlight?: boolean;
  onDelete?: (achievementId: string) => void;
}

const CAT_META: Record<JourneyCategory, { icon: React.ReactNode; tint: string; label: string }> = {
  metric: {
    label: 'Metric',
    tint: 'chart',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l6-6 4 4 8-8M14 7h7v7" />
      </svg>
    ),
  },
  milestone: {
    label: 'Milestone',
    tint: 'role-admin',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.5 5.5L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-1.5L12 2z" />
      </svg>
    ),
  },
  profile: {
    label: 'Profile',
    tint: 'muted',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 3.13l4.87 4.87M3 21l3.5-1 11.5-11.5L14 4 3 15v6z" />
      </svg>
    ),
  },
  document: {
    label: 'Document',
    tint: 'info',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6z" />
        <path d="M14 3v6h6M9 14h6M9 18h6" />
      </svg>
    ),
  },
  achievement: {
    label: 'Achievement',
    tint: 'warning',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
      </svg>
    ),
  },
};

const fmtFull = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const durationLabel = (days: number) => {
  if (days === 0) return 'Day Zero';
  if (days < 30) return `Day ${days}`;
  if (days < 365) {
    const m = Math.round(days / 30.44);
    return `Month ${m}`;
  }
  const yrs = Math.floor(days / 365);
  const remM = Math.round((days - yrs * 365) / 30.44);
  return remM === 0 ? `Year ${yrs}` : `Year ${yrs} · M${remM}`;
};

const fmtValue = (value: number, unit?: string) => {
  if (unit === '$') {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
    return `$${value}`;
  }
  return value.toLocaleString();
};

const tintClasses: Record<string, { bg: string; text: string; border: string; ring: string }> = {
  chart:        { bg: 'bg-chart/10',         text: 'text-chart',         border: 'border-chart/30',         ring: 'ring-chart/30' },
  'role-admin': { bg: 'bg-role-admin/12',    text: 'text-role-admin',    border: 'border-role-admin/30',    ring: 'ring-role-admin/30' },
  muted:        { bg: 'bg-muted/10',         text: 'text-muted',         border: 'border-line',             ring: 'ring-line' },
  info:         { bg: 'bg-info/10',          text: 'text-info',          border: 'border-info/30',          ring: 'ring-info/30' },
  warning:      { bg: 'bg-warning/10',       text: 'text-warning',       border: 'border-warning/30',       ring: 'ring-warning/30' },
};

export default function JourneyEventCard({ event, side, daysSinceStart, highlight, onDelete }: Props) {
  const meta = CAT_META[event.category];
  const tint = tintClasses[meta.tint];

  return (
    <div className={`flex w-full ${side === 'right' ? 'justify-end' : 'justify-start'}`}>
      <div className={`w-full md:w-[calc(50%-2rem)] ${side === 'right' ? 'md:pl-8' : 'md:pr-8'}`}>
        <div
          id={`journey-${event.id}`}
          className={`group rounded-card border bg-shell p-4 transition motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-lg ${
            highlight
              ? `${tint.border} shadow-lg ring-2 ${tint.ring}`
              : 'border-line hover:border-chart/30'
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`flex items-center justify-center w-7 h-7 rounded-full ${tint.bg} ${tint.text} flex-shrink-0`}>
                {meta.icon}
              </span>
              <div className="min-w-0">
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${tint.text}`}>
                  {meta.label}
                </div>
                <div className="text-[11px] text-muted">{fmtFull(event.date)}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-[10px] uppercase tracking-wider text-muted bg-surface/60 px-2 py-0.5 rounded-full whitespace-nowrap">
                {durationLabel(daysSinceStart)}
              </span>
              {event.achievementId && onDelete && (
                <button
                  onClick={e => { e.stopPropagation(); onDelete(event.achievementId!); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-danger/10 text-muted hover:text-danger"
                  title="Delete achievement"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <h4 className="text-sm font-semibold text-text leading-snug">
            {(event.category === 'milestone' || event.category === 'achievement') && event.badge && (
              <span className="mr-1.5">{event.badge}</span>
            )}
            {event.title}
          </h4>

          {(event.value != null || event.score != null) && (
            <div className="mt-2 flex items-baseline gap-1.5">
              {event.value != null && (
                <>
                  <span className="font-display text-2xl tabular-nums text-warning leading-none">
                    {fmtValue(event.value, event.unit)}
                  </span>
                  {event.unit && event.unit !== '$' && (
                    <span className="text-xs text-muted">{event.unit}</span>
                  )}
                </>
              )}
              {event.score != null && (
                <>
                  <span className="font-display text-2xl tabular-nums text-success leading-none">{event.score}</span>
                  <span className="text-sm text-muted">/100</span>
                </>
              )}
            </div>
          )}

          {event.summary && (
            <p className="text-xs text-muted leading-relaxed mt-1.5">{event.summary}</p>
          )}

          {event.category === 'profile' && event.valueFrom !== undefined && event.valueTo !== undefined && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-md bg-surface/60 border border-line px-2.5 py-1.5">
                <div className="text-muted uppercase tracking-wide">Before</div>
                <div className="text-text truncate" title={event.valueFrom || '—'}>
                  {event.valueFrom || '—'}
                </div>
              </div>
              <div className="rounded-md bg-chart/10 border border-chart/30 px-2.5 py-1.5">
                <div className="text-chart uppercase tracking-wide">After</div>
                <div className="text-text truncate" title={event.valueTo || '—'}>
                  {event.valueTo || '—'}
                </div>
              </div>
            </div>
          )}

          {event.category === 'document' && (
            <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-info bg-accent-soft border border-chart/20 rounded-md px-2 py-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
              <span className="font-medium text-text">{event.documentTitle ?? 'Document'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
